import { useEffect } from "react";
import {
  Flex,
  Icon,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiLock } from "react-icons/fi";
import {
  SourcingEventV2Dto,
  useListBidV2SForEventQuery,
} from "../../../autogen/bff-api";
import { AwardTab } from "./award/AwardTab";
import { BidsTab } from "./BidsTab";
import { CommunicationTab } from "./communication/CommunicationTab";
import { EvaluationTab } from "./evaluation/EvaluationTab";
import { RequestTab } from "./RequestTab";
import { QualificationTab } from "./qualification/QualificationTab";
import { DpsCompetitionsTab } from "./dps/DpsCompetitionsTab";
import { DpsMembersTab } from "./dps/DpsMembersTab";
import {
  hasTenderStageQualification,
  isDpsScheme,
  isTwoStageProcedure,
} from "../../sourcing-events/buyer/wizard/utils/procedure";
import { isBidDeadlinePassed } from "./supplierRows";
import { isEvaluationLocked } from "./qualification/stage";
import { useEventTabLabel } from "./useEventTabLabel";
import { useRoundSelection } from "./rounds/RoundSelectionContext";

// ADR-033 D1: negotiation is not a tab — the round pager scopes the tabs instead.
export type EventTab =
  | "request"
  | "qualification"
  | "dpsSuppliers"
  | "dpsCompetitions"
  | "bids"
  | "evaluation"
  | "award"
  | "communication"
  | "tasks"
  | "team";

const TABS: { key: EventTab; labelKey: string }[] = [
  { key: "request", labelKey: "Request" },
  { key: "qualification", labelKey: "Qualification" },
  { key: "dpsSuppliers", labelKey: "Suppliers" },
  { key: "dpsCompetitions", labelKey: "Competitions" },
  { key: "bids", labelKey: "Bids" },
  { key: "evaluation", labelKey: "Evaluation" },
  { key: "award", labelKey: "Award" },
  { key: "communication", labelKey: "Communication" },
  { key: "tasks", labelKey: "Tasks" },
  { key: "team", labelKey: "Team" },
];

// An ordning admits suppliers and hosts purchases; it never collects a tender or awards anything
// itself, so the tabs that would ask it to are absent (FOA § 26-7).
const SCHEME_ONLY_TABS: EventTab[] = ["dpsSuppliers", "dpsCompetitions"];
const TABS_HIDDEN_ON_A_SCHEME: EventTab[] = ["qualification", "bids", "evaluation", "award"];

export const DEFAULT_TAB: EventTab = "request";

export const parseEventTab = (value: string | null): EventTab => {
  const found = TABS.find((tab) => tab.key === value);
  return found ? found.key : DEFAULT_TAB;
};

const TabPlaceholder = ({ label }: { label: string }) => {
  const { t } = useTranslation();
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="2"
      py="16"
      rounded="xl"
      bg="smBackground"
      border="1px dashed"
      borderColor="smBorder"
      color="smMuted"
    >
      <Text fontWeight="medium">{label}</Text>
      <Text fontSize="sm">{t("Coming soon")}</Text>
    </Flex>
  );
};

interface Props {
  event: SourcingEventV2Dto;
  activeTab: EventTab;
  onTabChange: (tab: EventTab) => void;
}

export const EventTabs = ({ event, activeTab, onTabChange }: Props) => {
  const { t } = useTranslation();
  const tabLabel = useEventTabLabel();
  const { selectedRound } = useRoundSelection();
  const { data: bidsData } = useListBidV2SForEventQuery({
    eventId: event.id,
  });
  // The badge counts delivered bids, not suppliers — a number beside "Bids" is read as bids,
  // so invitations and interest registrations must not inflate it. Same reveal rule as BidsTab:
  // public bids stay hidden until the deadline, while a private buyer sees deliveries as they
  // arrive (the BFF is the real gate; this mirrors it client-side).
  const revealedBids =
    isBidDeadlinePassed(event) || event.eventContext.type === "Private" ? bidsData?.bids ?? [] : [];
  const deliveredBidCount = revealedBids.filter((bid) =>
    selectedRound ? bid.forNegotiationRound === selectedRound.id : bid.forNegotiationRound == null,
  ).length;
  const publicContext = event.eventContext.type === "Public" ? event.eventContext : null;
  const procedureType = publicContext?.procedureType;
  const scheme = isDpsScheme(procedureType);
  const schemeOpen =
    event.status === "PUBLISHED" &&
    publicContext?.dpsScheme != null &&
    new Date(publicContext.dpsScheme.validTo) > new Date();
  // Public procurements always qualify their suppliers — two-stage ones on the forespørsler,
  // single-stage ones on the tenders. Private competitions have no qualification phase.
  const tabs = TABS.filter((tab) => {
    if (SCHEME_ONLY_TABS.includes(tab.key)) return scheme;
    if (scheme) return !TABS_HIDDEN_ON_A_SCHEME.includes(tab.key);
    if (tab.key === "qualification") {
      return isTwoStageProcedure(procedureType) || hasTenderStageQualification(procedureType);
    }
    return true;
  });
  // Single-stage procedures open the evaluation by completing the qualification, so the tab
  // stays locked until the event leaves PUBLISHED (SM-867).
  const evaluationLocked = isEvaluationLocked(event);
  const rawIndex = tabs.findIndex((tab) => tab.key === activeTab);
  const index = Math.max(0, rawIndex);
  // A deep link can name a tab this event doesn't have (e.g. ?tab=qualification on a
  // private event) or one that is still locked — normalize the URL to a tab actually shown.
  useEffect(() => {
    if (rawIndex === -1) onTabChange(tabs[0].key);
    else if (evaluationLocked && activeTab === "evaluation") onTabChange("qualification");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawIndex, evaluationLocked, activeTab]);

  return (
    <Tabs
      index={index}
      onChange={(i) => onTabChange(tabs[i].key)}
      colorScheme="teal"
      isLazy
    >
      <TabList overflowX="auto" overflowY="hidden">
        {tabs.map((tab) => {
          const locked = tab.key === "evaluation" && evaluationLocked;
          return (
            <Tab
              key={tab.key}
              whiteSpace="nowrap"
              isDisabled={locked}
              title={locked ? String(t("Complete the qualification to open the evaluation.")) : undefined}
            >
              {locked && <Icon as={FiLock} boxSize="3" mr="1.5" />}
              {tabLabel(tab.labelKey)}
              {tab.key === "bids" && deliveredBidCount > 0 ? ` (${deliveredBidCount})` : ""}
            </Tab>
          );
        })}
      </TabList>
      <TabPanels>
        <TabPanel px="0">
          <RequestTab event={event} />
        </TabPanel>
        {tabs.slice(1).map((tab) => (
          <TabPanel key={tab.key} px="0">
            {tab.key === "qualification" ? (
              <QualificationTab event={event} />
            ) : tab.key === "dpsSuppliers" ? (
              <DpsMembersTab event={event} schemeOpen={schemeOpen} />
            ) : tab.key === "dpsCompetitions" ? (
              <DpsCompetitionsTab event={event} />
            ) : tab.key === "bids" ? (
              <BidsTab event={event} />
            ) : tab.key === "evaluation" ? (
              <EvaluationTab event={event} />
            ) : tab.key === "award" ? (
              <AwardTab event={event} />
            ) : tab.key === "communication" ? (
              <CommunicationTab event={event} />
            ) : (
              <TabPlaceholder label={tabLabel(tab.labelKey)} />
            )}
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};
